import { useCallback, useMemo } from "react";

import {
  faEdit,
  faFlask,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "motion/react";
import { useLocalStorage } from "usehooks-ts";

import type { ChartExploreContext } from "src/analytics/copilot/chartExploreContext";
import { useChartExplore } from "src/analytics/copilot/useChartExplore";
import { DashboardEditActions } from "src/analytics/dashboard/DashboardEditActions";
import { DashboardGrid } from "src/analytics/dashboard/DashboardGrid";
import { DashboardGridSkeleton } from "src/analytics/dashboard/DashboardGridSkeleton";
import { DashboardLastEditedInfo } from "src/analytics/dashboard/DashboardLastEditedInfo";
import { DashboardMetricsProvider } from "src/analytics/dashboard/DashboardMetricsContext";
import { DashboardRefreshButton } from "src/analytics/dashboard/DashboardRefreshButton";
import { DashboardTabs } from "src/analytics/dashboard/DashboardTabs";
import { defaultDashboardWindow } from "src/analytics/dashboard/dashboardFilterParams";
import { JustPinnedProvider } from "src/analytics/dashboard/JustPinnedContext";
import { useDashboard } from "src/analytics/dashboard/useDashboard";
import { useDashboardLayoutEditing } from "src/analytics/dashboard/useDashboardLayoutEditing";
import { useListDashboardsQuery } from "src/api/analytics-dashboard/dashboard";
import { isPerformanceDashboard } from "src/api/analytics-dashboard/fixedDashboards";
import { useFlow } from "src/api/queries";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { Realm } from "src/clients/analytics-dashboard";
import { CopilotButton } from "src/design-system/CopilotButton/CopilotButton";
import { CopilotSidebar } from "src/flow/analytics/CopilotSidebar";
import { useSpecialDomainLabels } from "src/flow/analytics/useSpecialDomainLabels";
import { useTrackCopilotOpened } from "src/flow/analytics/useTrackCopilotOpened";
import { FlowPageLayout } from "src/flow/FlowPageLayout";
import { useEnvironment } from "src/hooks/useEnvironment";
import { SubHeaderDatePicker } from "src/layout/SubHeader";
import { FlowPageParams } from "src/router/urls";
import { type StrictDateRange, useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const FlowAnalyticsContent: React.FC = () => {
  const { flow_id } = useParamsDecode<FlowPageParams>();
  const { data: flow } = useFlow(flow_id, {
    refetchOnMount: true,
  });
  const [environment, setEnvironment] = useEnvironment();
  const labelDictionaries = useSpecialDomainLabels(flow_id, flow?.name ?? "");
  const [isCopilotOpen, setIsCopilotOpen] = useLocalStorage(
    "flowAnalyticsCopilotOpen",
    false,
  );
  const openCopilot = useCallback(
    () => setIsCopilotOpen(true),
    [setIsCopilotOpen],
  );
  const { pendingChartContext, exploreWithCopilot, clearPendingContext } =
    useChartExplore(openCopilot);

  const trackCopilotOpened = useTrackCopilotOpened();
  const handleOpenCopilotFromHeader = useCallback(() => {
    trackCopilotOpened("header_button");
    openCopilot();
  }, [trackCopilotOpened, openCopilot]);
  const handleOpenCopilotFromDashboard = useCallback(() => {
    trackCopilotOpened("dashboard_empty_state");
    openCopilot();
  }, [trackCopilotOpened, openCopilot]);
  const handleExploreWithCopilot = useCallback(
    (context: ChartExploreContext) => {
      trackCopilotOpened("chart_explore");
      exploreWithCopilot(context);
    },
    [trackCopilotOpened, exploreWithCopilot],
  );

  const defaultWindow = useMemo<StrictDateRange>(
    () => defaultDashboardWindow(),
    [],
  );
  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow<StrictDateRange>(defaultWindow, "flowAnalyticsTimeWindow");

  const { data: dashboards } = useListDashboardsQuery(Realm.FLOWS, flow_id);
  const [activeDashboardId, setActiveDashboardId] = useLocalStorage<
    string | undefined
  >(`flowAnalyticsActiveDashboard:${flow_id}`, undefined);
  const resolvedDashboardId =
    activeDashboardId && dashboards?.some((d) => d.id === activeDashboardId)
      ? activeDashboardId
      : dashboards?.[0]?.id;
  const isReadOnlyDashboard = isPerformanceDashboard(
    Realm.FLOWS,
    resolvedDashboardId,
  );

  const editing = useDashboardLayoutEditing(
    Realm.FLOWS,
    flow_id,
    resolvedDashboardId,
  );
  const { dashboard } = useDashboard(Realm.FLOWS, flow_id, resolvedDashboardId);

  // Until the list resolves, the hooks above fall back to the default
  // dashboard, whose pins are not the stored selection's.
  const pins = resolvedDashboardId ? Object.values(dashboard.pins) : [];

  return (
    <JustPinnedProvider key={`${Realm.FLOWS}:${flow_id}`}>
      <DashboardMetricsProvider pins={pins} timeWindow={timeWindow}>
        <FlowPageLayout
          flow={flow}
          headerActions={
            <>
              <div className="pr-3">
                <DashboardLastEditedInfo
                  dashboardId={resolvedDashboardId}
                  realm={Realm.FLOWS}
                  realmId={flow_id}
                />
              </div>
              <AnimatePresence initial={false}>
                {!isCopilotOpen && !editing.isEditing && (
                  <m.div
                    key="ask-copilot"
                    className="pr-3"
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    initial={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                  >
                    <CopilotButton
                      dataLoc="ask-copilot-button"
                      onClick={handleOpenCopilotFromHeader}
                    >
                      Ask Copilot
                    </CopilotButton>
                  </m.div>
                )}
              </AnimatePresence>
            </>
          }
          rightPanel={
            flow && (
              <AnimatePresence initial={false} mode="wait">
                {isCopilotOpen && !editing.isEditing && (
                  <CopilotSidebar
                    key="copilot-sidebar"
                    dashboardId={resolvedDashboardId}
                    flowId={flow_id}
                    flowName={flow.name}
                    isLayoutEditing={editing.isEditing}
                    onClearPendingStructuredInput={clearPendingContext}
                    onClose={() => setIsCopilotOpen(false)}
                    pendingStructuredInput={pendingChartContext}
                  />
                )}
              </AnimatePresence>
            )
          }
        >
          <div className="sticky -top-5 z-10 -mx-5 -mt-5 mb-5">
            <DashboardTabs
              activeDashboardId={resolvedDashboardId ?? ""}
              canCreate={false}
              realm={Realm.FLOWS}
              realmId={flow_id}
              onDashboardChange={setActiveDashboardId}
            >
              {editing.isEditing ? (
                <DashboardEditActions
                  isSaving={editing.isSaving}
                  onCancel={editing.cancel}
                  onSave={editing.save}
                />
              ) : (
                <>
                  <SubHeaderDatePicker
                    placeholder="Last 30 days"
                    value={dateRangePickerValue}
                    onChange={onDateRangePickerChange}
                  />
                  <DashboardRefreshButton />
                  <span className="h-full w-12 border-gray-200 border-l p-3.5">
                    <EllipsisOptionsDropdown
                      dataLoc="flow-analytics-page-menu"
                      elements={[
                        ...(isReadOnlyDashboard
                          ? []
                          : [
                              {
                                key: "Organize dashboard",
                                icon: faEdit,
                                action: editing.start,
                                disabled: !editing.canEdit,
                              },
                            ]),
                        {
                          key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"}`,
                          icon:
                            environment === "sandbox" ? faWavePulse : faFlask,
                          action: () =>
                            setEnvironment(
                              environment === "sandbox" ? "live" : "sandbox",
                            ),
                        },
                      ]}
                      iconSize="sm"
                      placement="bottom start"
                    />
                  </span>
                </>
              )}
            </DashboardTabs>
          </div>
          {resolvedDashboardId ? (
            <DashboardGrid
              dashboardId={resolvedDashboardId}
              editLayout={editing.layout}
              isEditing={editing.isEditing}
              isSaving={editing.isSaving}
              labelDictionaries={labelDictionaries}
              onEditLayoutChange={editing.onLayoutChange}
              onExploreWithCopilot={handleExploreWithCopilot}
              onOpenCopilot={handleOpenCopilotFromDashboard}
              realm={Realm.FLOWS}
              realmId={flow_id}
            />
          ) : (
            <DashboardGridSkeleton />
          )}
        </FlowPageLayout>
      </DashboardMetricsProvider>
    </JustPinnedProvider>
  );
};
